/**
 * @fileoverview Modelo de navegação do slider de onboarding — PURO e testável.
 *
 * Mora fora do componente de propósito: o bug que este módulo fecha é de REGRA
 * (qual tecla faz o quê, e o que pode ser consequência de auto-repeat), não de
 * render. Regra em função pura = provável por teste, sem DOM e sem Norigin.
 *
 * ===== 1. UMA regra de tecla (não duas) =====
 * A linha de controles tem no máximo dois botões: `‹ Voltar` (esquerda) e
 * `Próximo ›`/`Começar ✓` (direita). A regra, única:
 *
 *   · seta que aponta PRO OUTRO BOTÃO  -> move o FOCO;
 *   · seta que SAI da linha de botões  -> anda o CARROSSEL, com BOUNCE nas pontas.
 *
 * É o mesmo modelo dos trilhos do app (seta na ponta da fileira = paginar), e
 * agora não tem exceção. Antes tinha: `→` no último slide não fazia bounce, ele
 * FINALIZAVA — commitava `markWelcomeSeen()` e saía da tela. Essa era a terceira
 * conduta da mesma tecla (foco / carrossel / commit irreversível) e é o que a
 * revisão de UX leu como "dois modelos mentais".
 *
 * Simetria PLENA (←/→ sempre a mesma classe de ação) é impossível com 2 botões
 * numa linha + carrossel: se `←` no "Próximo" andasse o carrossel, o "Voltar"
 * viraria um focável inalcançável por D-pad (anti-padrão pior). Então a regra
 * escolhida é a que não deixa nenhum controle órfão.
 *
 * ===== 2. Ação IRREVERSÍVEL não pode ser consequência de auto-repeat =====
 * Medição da revisão: 4 `ArrowRight` a 90ms (auto-repeat típico de D-pad) saíam
 * do slider em 871ms e gravavam `welcome-seen=true`. Segurar OK era ainda pior
 * (0→1→2→finish em ~180ms). Quem segura a seta perde a apresentação inteira, e
 * o BACK no `/account/gate` não traz de volta (o gate é raiz por desenho).
 *
 * O invariante NÃO é "qual tecla" (proxy), é "esta ação é reversível?":
 *   · trocar de slide  = reversível  -> ritmo mínimo, só pra não borrar a tela;
 *   · finalizar/pular  = IRREVERSÍVEL -> exige gesto deliberado.
 *
 * ===== 3. O invariante é "este keydown NÃO é repetição" (não "houve keyup") =====
 * A 1a versão deste guard usava keyup como prova de gesto deliberado: "soltou a
 * tecla, então o próximo keydown é press novo". O review derrubou, com razão:
 * `keyup` é PROXY, não o invariante. Existe firmware de TV que emite UM PAR
 * keydown+keyup POR REPETIÇÃO — nele o proxy fica ligado o tempo todo, o guard
 * vira no-op e segurar OK volta a gravar `markWelcomeSeen()` em 180ms, o número
 * exato de antes do fix. É a assinatura clássica: a otimização/poda dispara pela
 * PROXY em vez da CONSEQUÊNCIA, e o teste que deveria pegar rodou justamente na
 * única topologia onde a proxy funciona.
 *
 * O invariante é observável direto no keydown, em DUAS camadas independentes:
 *   (a) `KeyboardEvent.repeat === true` -> é repetição. Vale onde o motor
 *       preenche a flag;
 *   (b) intervalo desde o keydown ANTERIOR < DELIBERATE_GAP_MS -> é repetição.
 *       Cobre o motor que não preenche `repeat` (o auto-repeat medido é de 90ms;
 *       humano soltando e apertando de novo num controle de TV não chega perto
 *       de 200ms).
 * Nenhuma das duas depende de keyup, então as três classes de firmware (o que
 * nunca solta, o que emite keyup por repetição e o mudo) caem no MESMO caminho.
 *
 * E TEM QUE HAVER SAÍDA POR TEMPO: se um firmware marcar `repeat: true` até em
 * press legítimo, "deliberado" nunca aconteceria e o usuário ficaria preso na
 * PRIMEIRA tela do app. Por isso a condição final é
 * `(deliberado E slide assentado) OU tempo`, nunca só a primeira metade.
 *
 * Custo pro caso patológico (tecla presa, sem soltar): 300 + 1200 = 1,5s
 * ININTERRUPTOS pra perder o onboarding (era 180ms) — e agora isso vale pras
 * TRÊS classes de firmware, não só pra uma.
 */

/** Quantidade de slides do carrossel. Fonte única (a tela importa daqui). */
export const SLIDE_COUNT = 3

/**
 * Ritmo mínimo (ms) entre passos do carrossel sob tecla presa. 300ms ≈ a
 * transição de slide (260ms do slideIn) — o passo só acontece com o anterior já
 * assentado. Também é o "assentamento" exigido antes de finalizar: não se
 * commita um slide que ainda está entrando na tela. Press deliberado depois
 * disso não espera nada.
 */
export const STEP_ARM_MS = 300

/**
 * Saída por TEMPO da ação irreversível, pro firmware que marca `repeat: true`
 * até em press legítimo (aí "deliberado" nunca acontece). 1200ms = 13x o
 * auto-repeat medido (90ms) e ~4,6x a transição: ninguém lê um slide e decide em
 * menos que isso, e o app nunca fica preso na primeira tela.
 */
export const FINISH_ARM_MS = 1200

/**
 * Intervalo (ms) entre dois keydowns abaixo do qual o segundo é REPETIÇÃO, e
 * não gesto novo. O auto-repeat medido no laudo é 90ms; o piso físico de um
 * humano soltando e apertando de novo um controle de TV é ~150ms e na prática
 * muito mais. 200ms fica no meio, longe dos dois.
 */
export const DELIBERATE_GAP_MS = 200

/** Direções que o `onArrowPress` do Focusable entrega. */
export type ArrowDir = 'left' | 'right' | 'up' | 'down'

/** Os três focáveis da tela. */
export type WelcomeButton = 'back' | 'next' | 'skip'

/**
 * O que a seta faz. `edge` é o bounce declarado (ponta do carrossel): a tela
 * CONSOME a tecla (não devolve pro Norigin caçar foco que não existe) e não faz
 * nada — o rótulo "Começar ✓" e o 3º dot aceso já dizem que acabou.
 * `pass` devolve a decisão pro spatial nav.
 */
export type WelcomeNavAction =
  | { kind: 'slide'; to: number }
  | { kind: 'focus'; to: WelcomeButton }
  | { kind: 'edge' }
  | { kind: 'pass' }

/**
 * Resolve a seta. Puro: mesma entrada, mesma saída, sem tocar DOM/foco.
 * `slide` é o índice atual (0..SLIDE_COUNT-1).
 */
export function resolveArrow(
  button: WelcomeButton,
  dir: ArrowDir,
  slide: number,
): WelcomeNavAction {
  const isFirst = slide <= 0
  const isLast = slide >= SLIDE_COUNT - 1

  if (button === 'skip') {
    // O Pular só participa do eixo vertical; ←/→ ali não têm vizinho nem
    // significado de carrossel (ele não é a linha de controles).
    return dir === 'up' ? { kind: 'focus', to: 'next' } : { kind: 'pass' }
  }

  if (dir === 'down') return { kind: 'focus', to: 'skip' }
  if (dir === 'up') return { kind: 'pass' }

  if (button === 'next') {
    // ← aponta pro Voltar quando ele existe (slide 2+); no slide 1 não há
    // vizinho NEM slide anterior: bounce.
    if (dir === 'left') return isFirst ? { kind: 'edge' } : { kind: 'focus', to: 'back' }
    // → sai da linha: anda o carrossel. Na última, BOUNCE — nunca finaliza.
    return isLast ? { kind: 'edge' } : { kind: 'slide', to: slide + 1 }
  }

  // button === 'back' (só é renderizado a partir do slide 2)
  if (dir === 'right') return { kind: 'focus', to: 'next' }
  return isFirst ? { kind: 'edge' } : { kind: 'slide', to: slide - 1 }
}

/**
 * Rastreador de ativação: consome os eventos CRUS (keydown + troca de slide) e
 * responde se cada ação está liberada.
 *
 * Ele existe como objeto — e não como função pura recebendo um `state` que a
 * tela monta à mão — de propósito: a versão anterior fazia a tela REIMPLEMENTAR
 * a máquina de estado (`rearmed = false` escrito no componente), e o teste
 * reimplementava de novo. Resultado: dava pra apagar a linha do componente e a
 * suíte continuava 100% verde, com o bug de volta. Aqui a tela só REPASSA
 * eventos; a decisão inteira mora neste arquivo, que é o que o teste exercita.
 *
 * O relógio entra por parâmetro (`now`) pra o teste controlar o tempo sem fake
 * timer — e porque a tela passa `performance.now()`, que é MONOTÔNICO (o
 * `Date.now` anda pra trás quando o NTP acerta o relógio de uma TV recém-ligada,
 * que é exatamente o momento em que esta tela aparece).
 */
export interface ActivationTracker {
  /**
   * Um keydown chegou. `repeat` = `KeyboardEvent.repeat` (pode ser undefined em
   * motor antigo); `key` = `KeyboardEvent.key` (idem).
   */
  keyDown(now: number, repeat?: boolean, key?: string): void
  /** O carrossel andou. */
  slideChanged(now: number): void
  /** O último keydown foi gesto novo (não repetição)? */
  isDeliberate(): boolean
  /**
   * O último keydown veio depois de uma PAUSA, IGNORANDO a flag `repeat`?
   *
   * É o sinal de "a tecla foi solta e apertada de novo" pro firmware que mente
   * marcando `repeat: true` até em press legítimo. Toda saída-por-tempo desta
   * tela é condicionada a isto: tecla PRESA não tem pausa nenhuma, então o
   * tempo sozinho nunca destrava enquanto o dedo está no botão — era isso que
   * ainda deixava o dialog de sair piscar 1x a cada 1,2s.
   */
  isAfterPause(): boolean
  /** Passo de carrossel liberado? (ritmo mínimo — ver STEP_ARM_MS) */
  canStep(now: number): boolean
  /** Ação IRREVERSÍVEL (`markWelcomeSeen`) liberada? Ver o bloco 3 do cabeçalho. */
  canFinish(now: number): boolean
}

export function createActivationTracker(): ActivationTracker {
  // -Infinity: nada apertado e nenhum slide trocado ainda. O 1º toque de quem
  // acabou de chegar responde na hora, e "Pular" na entrada funciona de cara.
  let lastKeyDownAt = Number.NEGATIVE_INFINITY
  let lastSlideAt = Number.NEGATIVE_INFINITY
  let lastKey: string | undefined
  let deliberate = true
  let afterPause = true

  return {
    keyDown(now, repeat, key) {
      // TROCOU DE TECLA = press novo, por definição: auto-repeat repete a MESMA
      // tecla, nunca alterna. Sem isto, soltar a seta e apertar OK dentro de
      // 200ms era classificado como repetição e o OK virava no-op MUDO — press
      // engolido sem feedback, que é o pior tipo de "guard conservador".
      // Motor que não preenche `key` cai no gap de tempo (o `!==` fica falso
      // entre dois undefined), então o fallback é o comportamento anterior.
      const trocouDeTecla = key !== lastKey
      afterPause = trocouDeTecla || now - lastKeyDownAt >= DELIBERATE_GAP_MS
      deliberate = repeat !== true && afterPause
      lastKeyDownAt = now
      lastKey = key
    },
    slideChanged(now) {
      lastSlideAt = now
    },
    isDeliberate() {
      return deliberate
    },
    isAfterPause() {
      return afterPause
    },
    canStep(now) {
      return deliberate || now - lastSlideAt >= STEP_ARM_MS
    },
    canFinish(now) {
      const desdeOSlide = now - lastSlideAt
      // Deliberado NÃO basta sozinho: o slide também precisa estar assentado,
      // senão o mesmo trem de repetições que trouxe o usuário até o último
      // slide emenda no commit.
      // A 2a metade é a rede contra beco sem saída em firmware que marca
      // `repeat` até em press legítimo — mas ela exige PAUSA, senão viraria só
      // um atraso: com a tecla presa o tempo passava do mesmo jeito e o
      // onboarding se perdia sozinho em 1,5s. Tecla presa não tem pausa; soltar
      // e apertar de novo tem.
      return (
        (deliberate && desdeOSlide >= STEP_ARM_MS) ||
        (afterPause && desdeOSlide >= FINISH_ARM_MS)
      )
    },
  }
}

/** Relógio MONOTÔNICO (ver a nota em ActivationTracker). Chr38 já tem
 *  `performance.now`; o fallback existe pro ambiente de teste sem DOM. */
export function nowMs(): number {
  return typeof performance !== 'undefined' && typeof performance.now === 'function'
    ? performance.now()
    : Date.now()
}
